import { ContextMenu, ContextMenuItem, LockIcon, PinIcon, RoomIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { CSSProperties, MouseEventHandler, memo, RefObject } from 'react';

type ResizeHandleProps = {
  onClick: MouseEventHandler<HTMLDivElement>;
  onMouseDown: MouseEventHandler<HTMLDivElement>;
};

type TeachingBlockGridCardViewProps = {
  activeEdge: 'start' | 'end' | null;
  canResizeEnd: boolean;
  canResizeStart: boolean;
  cardClassName: string;
  cardRef: RefObject<HTMLDivElement | null>;
  cardStyle: CSSProperties;
  hasRooms: boolean;
  isLocked: boolean;
  isPinned: boolean;
  labelShort?: string;
  menu: ContextMenuItem[];
  menuClassName: string;
  onCardClick: MouseEventHandler<HTMLDivElement>;
  onCardDoubleClick: MouseEventHandler<HTMLDivElement>;
  onMouseEnter: MouseEventHandler<HTMLDivElement>;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
  onOpenChange: () => void;
  parent: HTMLElement | null;
  styles: CSSModuleClasses;
  title?: string;
  endHandleProps: ResizeHandleProps;
  startHandleProps: ResizeHandleProps;
};

export const TeachingBlockGridCardView = memo(
  ({
    activeEdge,
    canResizeEnd,
    canResizeStart,
    cardClassName,
    cardRef,
    cardStyle,
    hasRooms,
    isLocked,
    isPinned,
    labelShort,
    menu,
    menuClassName,
    onCardClick,
    onCardDoubleClick,
    onMouseEnter,
    onMouseLeave,
    onOpenChange,
    parent,
    styles,
    title,
    endHandleProps,
    startHandleProps,
  }: TeachingBlockGridCardViewProps) => {
    return (
      <ContextMenu className={menuClassName} data={menu} parent={parent} onOpenChange={onOpenChange}>
        <div
          ref={cardRef}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onCardClick}
          onDoubleClick={onCardDoubleClick}
          title={title}
          className={cardClassName}
          style={cardStyle}
        >
          {canResizeStart && (
            <div
              {...startHandleProps}
              // globale Marker-Klasse `tb-resize-handle`: bleibt auch bei aktivem
              // Wahlfach-Pick bedienbar (s. TeachingBlockGrid.module.scss).
              className={classNames('tb-resize-handle', styles['resize-handle'], styles['resize-handle-start'], {
                [styles['is-active']]: activeEdge === 'start',
              })}
            />
          )}
          {canResizeEnd && (
            <div
              {...endHandleProps}
              className={classNames('tb-resize-handle', styles['resize-handle'], styles['resize-handle-end'], {
                [styles['is-active']]: activeEdge === 'end',
              })}
            />
          )}
          <div className={styles.title}>{labelShort}</div>
          {!hasRooms || isPinned || isLocked ? (
            <div className={styles.info}>
              {isPinned && <PinIcon className={styles.icon} />}
              {isLocked && <LockIcon className={styles.icon} />}
              {!hasRooms && <RoomIcon className={styles.icon} />}
            </div>
          ) : null}
        </div>
      </ContextMenu>
    );
  },
);

TeachingBlockGridCardView.displayName = 'TeachingBlockGridCardView';
